import type WaDataGrid from "@web.awesome.me/webawesome-pro/dist/components/data-grid/data-grid.js";
import type WaDialog from "@web.awesome.me/webawesome-pro/dist/components/dialog/dialog.js";
import type WaPage from "@web.awesome.me/webawesome-pro/dist/components/page/page.js";
import htmx from "htmx.org";
import { html } from "lit";
import { checkNotNil } from "../../check-not-nil.js";
import { JsonBootstrapper } from "../../handlers/json-bootstrapper.js";
import { stateProperty } from "../../property.js";
import { Notes as Model } from "../../schemas/note.js";
import type { ParserSchema, ParserType } from "../../schemas/parser.js";
import { gridSearch } from "../../search.js";
import { WmElement } from "../element.js";
import type { WmDeletionRequest } from "./details.js";

type Notes = ParserType<typeof Notes>;
const Notes = stateProperty(Model.schema, { defaultValue: [] });

export class WmNotesGrid extends WmElement {
  static readonly TAG_NAME = "wm-notes-grid";

  static properties = {
    notes: Notes,
    isMobile: { attribute: false },
  };

  declare notes: Notes;
  declare isMobile: boolean;
  #pageObserver?: MutationObserver;
  #jsonBootstrapper: JsonBootstrapper<ParserSchema<typeof Notes>>;

  constructor() {
    super();
    this.#jsonBootstrapper = new JsonBootstrapper({
      schema: Notes.schema,
      callback: (v) => (this.notes = v),
      logger: this.logger,
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializePageView();
  }

  disconnectedCallback() {
    this.#pageObserver?.disconnect();
    this.#pageObserver = undefined;
    super.disconnectedCallback();
  }

  protected render() {
    return html`
      <slot @slotchange=${(e: Event) => this.#jsonBootstrapper.slotChanged(e)}></slot>
      ${this.#renderGrid()}`;
  }

  #renderGrid() {
    return html`
      <wa-dialog label="Are you sure?">
        This action is irreversible.
        <div slot="footer" class="wa-cluster">
          <wa-button variant="brand" data-dialog="close">Cancel</wa-button>
          <wa-button id="deletion-confirmation" variant="danger" @click=${this.#confirmDeletion}>Confirm</wa-button>
        </div>
      </wa-dialog>
      <wa-data-grid
        label="Notes"
        paginate
        page-size="10"
        resizable
        row-key="id"
        size="s"
        striped
        with-search
      >
        <span slot="empty">You ain't got no notes.</span>
      </wa-data-grid>
      `;
  }

  protected updated(): void {
    this.#handleUpdated();
  }

  async #handleUpdated() {
    const dataGrid = this.#queryGrid();
    await dataGrid.updateComplete;
    dataGrid.searchFn = gridSearch;
    dataGrid.columns = [
      {
        field: "title",
        label: "Title",
        formatter: (v, row) =>
          html`<a href="/app/note/${row.id}" @click=${this.#titleClickHandler(row.id as string)}>${v}</a>`,
      },
      {
        field: "tags",
        label: "Tags",
        hidden: this.isMobile,
        formatter: (v) => html`<wm-tag-cell .tags=${v}></wm-tag-cell>`,
      },
      {
        id: "actions",
        label: "Actions",
        hidden: this.isMobile,
        formatter: (_, row) => html`
          <wm-delete-button size="xs" as-icon @click=${() => this.#promptDeletionConfirmation(row.id as string)}>
          </wm-delete-button>
        `,
      },
    ];
    dataGrid.rowDetail = (row) => html`
      <wm-note-details .details=${row} @wm-deletion-request=${(e: WmDeletionRequest) => this.#promptDeletionConfirmation(e.id)}></wm-note-details>
    `;
    dataGrid.data = this.notes;
  }

  #confirmDeletion() {
    const dialog = this.#queryDialog();
    const noteId = dialog.dataset.noteId;
    htmx.ajax("DELETE", `/app/notes/${noteId}`);
  }

  #promptDeletionConfirmation(noteId: string) {
    const dialog = this.#queryDialog();
    dialog.dataset.noteId = noteId;
    dialog.open = true;
  }

  #initializePageView() {
    // At this time, we actually consider it an error if no wraping wa-page
    // can be found.
    const page = this.#queryPage();
    this.isMobile = page.view === "mobile";
    this.#pageObserver = new MutationObserver(() => {
      this.isMobile = page.view === "mobile";
    });
    this.#pageObserver.observe(page, {
      attributes: true,
      attributeFilter: ["view"],
    });
  }

  #queryDialog(): WaDialog {
    return this.queryNotNil("wa-dialog");
  }

  #queryGrid(): WaDataGrid {
    return this.queryNotNil("wa-data-grid");
  }

  #queryPage(): WaPage {
    return checkNotNil(document.querySelector("wa-page"));
  }

  #titleClickHandler(id: string) {
    return (e: PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      htmx.ajax("GET", `/app/notes/${id}`, {
        target: "body",
        push: "true",
      });
    };
  }
}

customElements.define(WmNotesGrid.TAG_NAME, WmNotesGrid);

declare global {
  interface HTMLElementTagNameMap {
    [WmNotesGrid.TAG_NAME]: WmNotesGrid;
  }
}
